import type { LibraryItem, LibraryItemKind, SavedElements, SavedLook } from "@cva/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { client, orpc } from "../lib/api";
import { errorMessage } from "../lib/utils";

/** Saved building blocks and looks of the organization ("Meine Bausteine", "Meine Looks"). */

export function useLibraryItems<K extends LibraryItemKind>(orgId: string, kind: K) {
  const query = useQuery(orpc.library.list.queryOptions({ input: { orgId, kind } }));
  return {
    ...query,
    items: (query.data ?? []) as Extract<LibraryItem, { kind: K }>[],
  };
}

export type SaveInput =
  | { kind: "element"; name: string; data: SavedElements }
  | { kind: "look"; name: string; data: SavedLook };

export function useLibraryActions(orgId: string) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.library.key() });
  const save = useMutation({
    mutationFn: (input: SaveInput) => client.library.create({ orgId, ...input }),
    onSuccess: (item) => {
      void refresh();
      toast.success(
        item.kind === "element"
          ? `„${item.name}“ liegt jetzt unter Bausteine → Meine Bausteine`
          : `Look „${item.name}“ gespeichert`,
      );
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const rename = useMutation({
    mutationFn: (input: { itemId: string; name: string }) =>
      client.library.rename({ orgId, ...input }),
    onSuccess: () => void refresh(),
    onError: (error) => toast.error(errorMessage(error)),
  });
  const remove = useMutation({
    mutationFn: (itemId: string) => client.library.remove({ orgId, itemId }),
    onSuccess: () => void refresh(),
    onError: (error) => toast.error(errorMessage(error)),
  });
  return { save, rename, remove };
}
